export const APOS_STATE_KEYS = [
  "angry-irritable",
  "anxious-overwhelmed",
  "restless-activated",
  "sad-low",
  "numb-withdrawn",
  "depleted",
  "calm-steady",
  "positive-energized",
];

export const STATE_KNOWLEDGE = {
  "angry-irritable": {
    label: "Angry or irritable",
    system: "High sympathetic activation",
    target: "organize-high-activation",
    defaultOutput: "high-output",
    participantBiology:
      "This type of state commonly involves increased alertness, heart rate, muscular readiness, and stress hormones such as adrenaline and cortisol so the body can act.",
    clinicianBiology:
      "The self-reported pattern is consistent with sympathetic mobilization, commonly associated with increased catecholamine activity and activation of the stress-response system, including cortisol release.",
    targetExplanation:
      "Provide the fight-or-flight activation with a purposeful physical output, then complete a controlled cooldown that supports parasympathetic recovery.",
  },
  "restless-activated": {
    label: "Restless or activated",
    system: "High sympathetic activation",
    target: "organize-high-activation",
    defaultOutput: "high-output",
    participantBiology:
      "This type of state commonly involves increased alertness, heart rate, muscular readiness, and stress hormones such as adrenaline and cortisol so the body can act.",
    clinicianBiology:
      "The self-reported pattern is consistent with sympathetic mobilization, commonly associated with increased catecholamine activity and activation of the stress-response system, including cortisol release.",
    targetExplanation:
      "Provide the available activation with an organized physical task, then gradually reduce output to support parasympathetic recovery.",
  },
  "anxious-overwhelmed": {
    label: "Anxious or overwhelmed",
    system: "High sympathetic demand",
    target: "organize-overwhelm",
    defaultOutput: "moderate-output",
    participantBiology:
      "This type of state commonly involves increased scanning, alertness, heart rate, muscular tension, and stress hormones such as adrenaline and cortisol.",
    clinicianBiology:
      "The self-reported pattern is consistent with sympathetic mobilization and elevated stress-system demand. The intervention emphasizes rhythmic output and reduced complexity before recovery.",
    targetExplanation:
      "Use repetitive, controlled movement to organize attention and activation, followed by a deliberate recovery transition.",
  },
  "sad-low": {
    label: "Sad or low",
    system: "Reduced activation and engagement",
    target: "increase-activation",
    defaultOutput: "moderate-output",
    participantBiology:
      "This type of state commonly involves reduced spontaneous movement, lower available energy, and less engagement with the environment.",
    clinicianBiology:
      "The self-reported pattern is consistent with reduced behavioural and physiological activation. The selected input emphasizes circulation, rhythm, environmental engagement, and achievable participation.",
    targetExplanation:
      "Increase circulation, movement, and engagement with an accessible input that is easy to begin and repeat.",
  },
  "numb-withdrawn": {
    label: "Numb or withdrawn",
    system: "Reduced engagement",
    target: "increase-activation",
    defaultOutput: "low-activation",
    participantBiology:
      "This type of state commonly involves reduced movement, lower environmental engagement, and a tendency to pull away from activity or connection.",
    clinicianBiology:
      "The self-reported pattern is consistent with reduced engagement and behavioural withdrawal. The intervention uses a low-cost activation signal and a clear endpoint.",
    targetExplanation:
      "Introduce a small, receivable movement or environmental signal that interrupts withdrawal without creating an excessive demand.",
  },
  depleted: {
    label: "Depleted",
    system: "Low available activation",
    target: "restore-capacity",
    defaultOutput: "low-activation",
    participantBiology:
      "This type of state commonly involves low available energy, reduced movement, and limited capacity for demanding output.",
    clinicianBiology:
      "The self-reported pattern is consistent with low available capacity. The intervention prioritizes a small activation signal, preparation, and recovery rather than fatigue.",
    targetExplanation:
      "Use a small, repeatable activation signal and stop before the input becomes another draining demand.",
  },
  "calm-steady": {
    label: "Calm or steady",
    system: "Working regulatory range",
    target: "maintain-capacity",
    defaultOutput: "maintenance",
    participantBiology:
      "This type of state usually reflects enough available regulation to maintain normal activity or practise a controlled challenge.",
    clinicianBiology:
      "The self-reported pattern is consistent with a functional working range. The intervention may maintain capacity or introduce one controlled progression variable.",
    targetExplanation:
      "Maintain the useful state with familiar activity, or introduce one controlled challenge while preserving recovery.",
  },
  "positive-energized": {
    label: "Positive or energized",
    system: "Available adaptive capacity",
    target: "use-capacity",
    defaultOutput: "maintenance",
    participantBiology:
      "This type of state commonly includes available energy, motivation, and readiness for purposeful activity.",
    clinicianBiology:
      "The self-reported pattern is consistent with available adaptive capacity. The intervention can support performance, skill, or maintenance while retaining a planned recovery phase.",
    targetExplanation:
      "Use the available capacity for purposeful activity while keeping the dose controlled and recoverable.",
  },
};

export const FALLBACK_LEVELS = [
  "primary",
  "moderate",
  "selected-activity",
  "similar-activity",
  "somatic",
  "minimum",
];

export const FALLBACK_LABELS = {
  primary: "Best state-matched output",
  moderate: "Familiar moderate output",
  "selected-activity": "Selected sport or recreation",
  "similar-activity": "Similar sport, game, or skill",
  somatic: "Somatic, breathing, or mobility option",
  minimum: "Minimum useful input",
};

export const ENVIRONMENT_OUTPUTS = {
  outside: {
    "high-output": {
      title: "Controlled high-output outdoor exercise",
      examples: "Use hill intervals, controlled running intervals, fast cycling, or another familiar high-output outdoor activity.",
    },
    "moderate-output": {
      title: "Purposeful outdoor rhythmic movement",
      examples: "Use brisk walking, steady cycling, easy running, or another familiar rhythmic activity.",
    },
    "low-activation": {
      title: "Short outdoor activation",
      examples: "Take an easy purposeful walk, use gentle mobility outdoors, or move with someone at an accessible pace.",
    },
    maintenance: {
      title: "Familiar outdoor activity",
      examples: "Choose a familiar walk, run, ride, sport, skill activity, or recreational session.",
    },
  },
  gym: {
    "high-output": {
      title: "Controlled high-output gym exercise",
      examples: "Use treadmill incline intervals, cycling intervals, rowing intervals, bag work, or a familiar conditioning circuit.",
    },
    "moderate-output": {
      title: "Moderate rhythmic gym exercise",
      examples: "Use a treadmill, bike, rower, elliptical, or familiar full-body circuit at a controlled pace.",
    },
    "low-activation": {
      title: "Low-cost gym activation",
      examples: "Use easy cycling, treadmill walking, mobility, or a short familiar resistance sequence.",
    },
    maintenance: {
      title: "Familiar gym session",
      examples: "Use a familiar aerobic, resistance, technical, or class-based activity.",
    },
  },
  inside: {
    "high-output": {
      title: "Controlled high-output indoor exercise",
      examples: "Use shadowboxing, a familiar bodyweight circuit, fast marching, step work, or another controlled whole-body activity.",
    },
    "moderate-output": {
      title: "Moderate indoor rhythmic movement",
      examples: "Use marching, dancing, a familiar circuit, indoor walking, or controlled technical movement.",
    },
    "low-activation": {
      title: "Short indoor activation",
      examples: "Use gentle marching, mobility, music-supported movement, or a short indoor walk.",
    },
    maintenance: {
      title: "Familiar indoor activity",
      examples: "Choose a familiar mobility, aerobic, strength, skill, or recreational activity.",
    },
  },
  limited: {
    "high-output": {
      title: "High-output movement in limited space",
      examples: "Use fast marching, controlled shadowboxing, repeated sit-to-stand work, or another familiar compact-space activity.",
    },
    "moderate-output": {
      title: "Moderate movement in limited space",
      examples: "Use marching, seated or standing rhythmic movement, mobility, or a compact familiar circuit.",
    },
    "low-activation": {
      title: "Small activation signal",
      examples: "Use seated rhythmic movement, gentle mobility, or repeated position changes.",
    },
    maintenance: {
      title: "Compact-space maintenance",
      examples: "Use a familiar short mobility, strength, skill, or rhythmic movement sequence.",
    },
  },
};

export const SOMATIC_OPTIONS = {
  "organize-high-activation": {
    title: "Somatic downshift with movement",
    instruction:
      "Breathe slowly for two minutes, then complete gentle shoulder, trunk, and hip movement before finishing with an easy two-minute walk or seated rhythmic movement.",
    duration: 6,
  },
  "organize-overwhelm": {
    title: "Orient, breathe, and move slowly",
    instruction:
      "Reduce visual stimulation, orient to the room, breathe slowly for two minutes, and complete gentle repetitive movement.",
    duration: 5,
  },
  "increase-activation": {
    title: "Somatic activation sequence",
    instruction:
      "Sit or stand upright, take several comfortable breaths, move the shoulders and trunk, then complete two minutes of easy rhythmic movement.",
    duration: 5,
  },
  "restore-capacity": {
    title: "Low-demand body reset",
    instruction:
      "Change position, breathe comfortably, and complete gentle range-of-motion movements without trying to create fatigue.",
    duration: 4,
  },
  "maintain-capacity": {
    title: "Brief mobility and breathing reset",
    instruction:
      "Complete two minutes of easy mobility and one minute of comfortable slower breathing.",
    duration: 3,
  },
  "use-capacity": {
    title: "Brief mobility and breathing reset",
    instruction:
      "Complete two minutes of easy mobility and one minute of comfortable slower breathing.",
    duration: 3,
  },
};

export const MINIMUM_INPUTS = {
  "organize-high-activation":
    "Take five slower breaths, release your shoulders, and change rooms or step outside briefly before checking in again.",
  "organize-overwhelm":
    "Reduce one source of stimulation, orient to the space around you, and take five comfortable slower breaths.",
  "increase-activation":
    "Stand up or change position, move for one minute, and get natural light or fresh air when available.",
  "restore-capacity":
    "Change position, take a few comfortable breaths, and complete one minute of gentle movement.",
  "maintain-capacity":
    "Complete one minute of comfortable movement and return to the day with a clear next action.",
  "use-capacity":
    "Complete one minute of comfortable movement and return to the day with a clear next action.",
};

export const MORNING_ROUTINES = {
  "high-sympathetic":
    "Drink water, use two to five minutes of comfortable slower breathing, and complete gentle mobility or a short intentional walk. This prepares the day and does not replace the main intervention.",
  "reduced-activation":
    "Drink water, seek natural light when available, and use a short purposeful walk or rhythmic mobility sequence to increase engagement.",
  steady:
    "Drink water and complete a short mobility or walking routine to maintain a useful starting rhythm.",
};

export const BEDTIME_ROUTINES = {
  default:
    "After the final meal, take an easy intentional walk when practical. Near bedtime, use slow breathing, gentle stretching, mobility, or a low-stimulation somatic routine.",
  limited:
    "Near bedtime, reduce stimulation and complete two to five minutes of comfortable breathing or gentle mobility.",
};
